/* eslint-disable @typescript-eslint/no-empty-object-type */
import { useCustomToast } from '@/hooks/use-custom-toast';
import React, { memo, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  CreateLabelCaseValidationSchema,
  TCreateLabelCaseValidation,
  useCreateLabelCaseMutation,
} from '../../../api/create-label-case.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_CASE_QUERY_KEY } from '../../../api/get-list-label-case.api';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { handleDomBubbleEvent } from '@/utils/dom';
// import { GENERAL_LABEL_COLOR } from '@/features/settings/label/_shared/constants/general-label-color';
import { GENERAL_LABEL_COLOR } from '@/features/cases/_shared/constants/cases.contants';
import { cn } from '@/utils/cn';
import { Input } from '@/components/_ui/input';
import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

export type TCreateLabelCaseModalType = 'CreateLabelCaseModal';
export interface ICreateLabelCaseModalProps {}

const CreateLabelCaseModal = memo((_props: ICreateLabelCaseModalProps) => {
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const isOpenCreateLabelCaseModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('CreateLabelCaseModal');
  }, [caseModuleModalRootActions]);
  const showToast = useCustomToast();
  const form = useForm<TCreateLabelCaseValidation>({
    resolver: zodResolver(CreateLabelCaseValidationSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      color: '',
    },
  });

  const mutationCreateLabelCase = useCreateLabelCaseMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error adding label, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_CASE_QUERY_KEY] })]);
        caseModuleModalRootActions.closeModal('CreateLabelCaseModal');
        form.reset();
        showToast({
          message: 'Label case created successfully',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitCreateLabelCase = useCallback(
    (data: TCreateLabelCaseValidation) => {
      mutationCreateLabelCase.mutate({
        name: data?.name,
        color: data?.color,
      });
    },
    [mutationCreateLabelCase]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('CreateLabelCaseModal');
    form.reset();
  }, [caseModuleModalRootActions, form]);

  return (
    <Modal isOpen={isOpenCreateLabelCaseModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Add Label Case</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitCreateLabelCase)(e);
          }}
          id="create-label-case-form"
          className="grid gap-[16px]"
        >
          <div className="grid gap-[4px]">
            <label htmlFor="name-label-case" className="text-[#4B4B4B] text-[14px] font-normal">
              Name*
            </label>
            <Input id="name-label-case" placeholder="Enter Label Case Name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="color-label-case" className="text-[#4B4B4B] text-[14px] font-normal">
              Color*
            </label>
            <div id="color-label-case" className="grid grid-cols-3 gap-[22px]">
              {Object.entries(GENERAL_LABEL_COLOR).map(([key, color], index) => {
                const isSelected = form.watch('color') === color;
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      form.setValue('color', color);
                    }}
                    key={index + 1}
                    className={cn(
                      'w-full gap-[6px] flex justify-start py-[8px] px-[6px] rounded-[8px] bg-[#F1F5F9] cursor-pointer border-transparent border-2 hover:border-[#0C8DBF] hover:bg-[#0C8DBF]/20 transition-all duration-300',
                      isSelected && 'border-[#0C8DBF] bg-[#0C8DBF]/20'
                    )}
                  >
                    <div className="w-[16px] h-[16px] rounded-full " style={{ backgroundColor: color }}></div>
                    <span className="text-[#25151E] text-[12px] font-normal">{key}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="create-label-case-form" type="submit" disabled={mutationCreateLabelCase.isPending}>
          {mutationCreateLabelCase.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Save
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
CreateLabelCaseModal.displayName = 'CreateLabelCaseModal';

export default CreateLabelCaseModal;
